import { Link } from 'react-router-dom'
import '@/styles/aura-auth.css'

interface ErrorFallbackProps {
  resetError?: () => void
}

/**
 * Branded full-page fallback for unhandled render errors (Sentry ErrorBoundary).
 * Shows generic copy only — the error object itself goes to Sentry; raw
 * exception text can be minified internals and means nothing to users.
 * Uses the auth world's centered layout: an error boundary can fire on any
 * page, so it must not depend on the shell's context providers being alive.
 */
export function ErrorFallback({ resetError }: ErrorFallbackProps) {
  return (
    <div className="aura-auth-page">
      <div className="auth-ambient" aria-hidden="true"></div>
      <div className="auth-mesh" aria-hidden="true"></div>
      <main className="auth-inner" style={{ paddingTop: 64, paddingBottom: 64 }}>
        <Link to="/" className="auth-brand" aria-label="Aura home">
          <span className="mark" aria-hidden="true"></span>
          <span>Aura</span>
        </Link>
        <div className="card auth-card" style={{ textAlign: 'center', alignItems: 'center' }}>
          <div>
            <h1>This page hit an error</h1>
            <p className="auth-sub">
              Something went wrong on this page. The issue has been logged. Try reloading —
              if it keeps happening, head back home and pick up where you left off.
            </p>
          </div>
          <div style={{ display: 'flex', gap: 12, justifyContent: 'center', flexWrap: 'wrap' }}>
            {/* .btn, not .wbtn: this file must not depend on aura-app.css —
                when the boundary fires on the landing/auth pages the shell
                sheet isn't loaded, and aura-base.css (global in main.tsx)
                is the only styling that is guaranteed to be there. */}
            <button
              type="button"
              className="btn btn-primary"
              onClick={() => {
                if (resetError) {
                  resetError()
                } else {
                  window.location.reload()
                }
              }}
            >
              Try again
            </button>
            <Link to="/" className="btn btn-ghost">
              Back to home
            </Link>
          </div>
        </div>
      </main>
    </div>
  )
}
